import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { logActivity, getActivityList, getActivityCatalog, getFrequentActivities } from '../api/activities';
import { formatActivityName } from '../utils';
import AnswerChoiceGroup from './AnswerChoiceGroup';
import EffortScale from './EffortScale';

const SPUD_QUOTES = [
    'Spud-Tastic!',
    'You Roasted That Routine!',
    "You're Mashing Milestones!",
    'Sweat Now, Snack Later.',
    "You're on a Roll, Sweet Potato!",
    'You Crushed It, Couch Potato No More!',
    'Peel the Burn, Feel the Gain!',
    'Eyes on the Fries, Heart on the Prize!',
    "You're Hashing Out Greatness!",
    'Smashed that session like mashed potatoes!',
    "You're Golden and Roasted!",
    'No Small Fries Energy Today!',
    'Rooting for You — and You Delivered!',
    'Starch Today, Strong Tomorrow.',
    "You're Frying the Competition!",
    'Earned it. Burned it. Spud it.',
    'From Couch Potato to Power Potato!',
    'That workout was Un-Peel-ievable!',
    'Hot Potato? More like Hot Streak!',
    'Rooted in Discipline.',
    "Rep by Rep, You're Getting Crispy!",
    'You roasted that routine.',
    'Keep digging — greatness is growing.',
    'Baked, not broken.',
    "You're shredding like hash browns!",
    'Reps before chips.',
    'Sweet gains, sweet potato.',
    'Keep planting effort, keep harvesting wins.',
];

/* ─── Dual scroll-wheel time picker (hours + minutes) ─── */
const HOURS_OPTIONS = Array.from({ length: 24 }, (_, i) => i);   // 0–23
const MINS_OPTIONS  = Array.from({ length: 60 }, (_, i) => i);   // 0–59

const ScrollWheel = ({ options, value, onChange, label, format }) => {
    const listRef = useRef(null);
    const ITEM_H = 44;
    const isScrolling = useRef(false);
    const scrollTimer = useRef(null);
    // Triple the options for seamless wrapping
    const tripled = [...options, ...options, ...options];
    const N = options.length;

    const safeIdx = Math.max(0, options.indexOf(Number(value) || 0));

    // Initial scroll to the middle copy
    useEffect(() => {
        if (!isScrolling.current && listRef.current) {
            listRef.current.scrollTo({ top: (N + safeIdx) * ITEM_H, behavior: 'auto' });
        }
    }, [safeIdx, N]);

    const handleScroll = useCallback(() => {
        isScrolling.current = true;
        clearTimeout(scrollTimer.current);
        scrollTimer.current = setTimeout(() => {
            if (!listRef.current) return;
            const rawIdx = Math.round(listRef.current.scrollTop / ITEM_H);
            // Snap within tripled list
            listRef.current.scrollTo({ top: rawIdx * ITEM_H, behavior: 'smooth' });
            const wrappedIdx = ((rawIdx % N) + N) % N;
            onChange(options[wrappedIdx]);

            // Silently jump to middle copy so there's always room to scroll both ways
            if (rawIdx < N || rawIdx >= N * 2) {
                setTimeout(() => {
                    if (!listRef.current) return;
                    listRef.current.scrollTo({ top: (N + wrappedIdx) * ITEM_H, behavior: 'auto' });
                }, 120);
            }
            isScrolling.current = false;
        }, 80);
    }, [onChange, options, N]);

    return (
        <div className="la-wheel-wrapper">
            <div className="la-wheel-label">{label}</div>
            <div className="la-time-picker">
                <div className="la-time-highlight" />
                <div className="la-time-list" ref={listRef} onScroll={handleScroll}>
                    <div style={{ height: ITEM_H * 2 }} />
                    {tripled.map((opt, i) => (
                        <div
                            key={i}
                            className={`la-time-item${opt === (Number(value) || 0) ? ' selected' : ''}`}
                            style={{ height: ITEM_H }}
                            onClick={() => {
                                listRef.current?.scrollTo({ top: (N + options.indexOf(opt)) * ITEM_H, behavior: 'smooth' });
                                onChange(opt);
                            }}
                        >
                            {format(opt)}
                        </div>
                    ))}
                    <div style={{ height: ITEM_H * 2 }} />
                </div>
            </div>
        </div>
    );
};

const TimeScrollPicker = ({ hours, minutes, onHoursChange, onMinutesChange }) => (
    <div className="la-dual-wheel">
        <ScrollWheel
            options={HOURS_OPTIONS}
            value={hours}
            onChange={onHoursChange}
            label="hrs"
            format={(h) => `${h}`}
        />
        <div className="la-wheel-sep">:</div>
        <ScrollWheel
            options={MINS_OPTIONS}
            value={minutes}
            onChange={onMinutesChange}
            label="min"
            format={(m) => String(m).padStart(2, '0')}
        />
    </div>
);

const DURATION_BASIS_LABELS = {
    session_time: 'Count the whole session',
    active_time: 'Count active time only',
};

const getLocalDateString = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const INITIAL_FORM = {
    activityId: '',
    primaryAnswerCode: null,
    secondaryAnswerCode: null,
    hours: 0,
    minutes: 0,
    personalEffort: null,
    date: getLocalDateString(),
};

/**
 * Log Activity form (sp_v1_0).
 *
 * Flow (wiki/technical/spud-points.md section 5):
 *   activity → primary question (if any) → secondary question (if any) → duration → Personal Effort → result
 *
 * Each section is revealed once the previous one is complete. The backend
 * resolves Reference MET and computes SP; this form only collects inputs.
 */
const LogActivityForm = ({ onSuccess }) => {
    const history = useHistory();
    const [form, setForm] = useState(INITIAL_FORM);
    const [activities, setActivities] = useState([]);
    const [frequentActivities, setFrequentActivities] = useState([]);
    const [catalog, setCatalog] = useState(null);
    const [catalogError, setCatalogError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [popup, setPopup] = useState({ show: false, fadeOut: false, quote: '', points: 0, activityId: null });

    // Activity search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const searchWrapperRef = useRef(null);

    useEffect(() => {
        const fetchActivities = async () => {
            try {
                const [list, frequent] = await Promise.all([
                    getActivityList(),
                    getFrequentActivities(),
                ]);
                setActivities(Array.isArray(list) ? list : []);
                setFrequentActivities(Array.isArray(frequent) ? frequent : []);
            } catch (err) {
                setActivities([]);
                setFrequentActivities([]);
            }
        };
        fetchActivities();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
                setSearchOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const activityById = useMemo(
        () => Object.fromEntries(activities.map((a) => [a.activity_id, a])),
        [activities]
    );

    const displayName = useCallback(
        (activityId) => activityById[activityId]?.activity_name || formatActivityName(activityId),
        [activityById]
    );

    // Build search results from query
    const searchResults = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const byName = (a, b) => a.activity_name.localeCompare(b.activity_name);

        if (!q) {
            // Frequent activities first (only those still in the catalog), then all sorted alphabetically
            const freqSet = new Set(frequentActivities);
            const freqResults = frequentActivities
                .filter((id) => activityById[id])
                .map((id) => ({ activity: activityById[id], matchedVia: null }));
            const allResults = [...activities]
                .filter((a) => !freqSet.has(a.activity_id))
                .sort(byName)
                .map((a) => ({ activity: a, matchedVia: null }));
            return [...freqResults, ...allResults];
        }

        const nameMatches = [];
        const termMatches = [];
        for (const a of [...activities].sort(byName)) {
            const name = a.activity_name.toLowerCase();
            if (name.includes(q)) {
                nameMatches.push({ activity: a, matchedVia: null });
                continue;
            }
            // search_terms are activity_tags only (not question/answer text)
            const hit = (a.search_terms || []).find((t) => t.toLowerCase().includes(q));
            if (hit) {
                termMatches.push({ activity: a, matchedVia: hit });
            }
        }
        return [...nameMatches, ...termMatches];
    }, [searchQuery, activities, frequentActivities, activityById]);

    const handleSelectActivity = (activityId) => {
        setForm((prev) => ({
            ...prev,
            activityId,
            primaryAnswerCode: null,
            secondaryAnswerCode: null,
        }));
        setSearchQuery('');
        setSearchOpen(false);
        setMessage('');
    };

    const handleClearActivity = () => {
        setForm((prev) => ({
            ...prev,
            activityId: '',
            primaryAnswerCode: null,
            secondaryAnswerCode: null,
        }));
        setCatalog(null);
        setCatalogError('');
        setSearchQuery('');
    };

    // Load questions / answers / supported combinations for the chosen activity.
    useEffect(() => {
        let cancelled = false;
        const fetchCatalog = async () => {
            if (!form.activityId) {
                setCatalog(null);
                setCatalogError('');
                return;
            }
            try {
                const data = await getActivityCatalog(form.activityId);
                if (!cancelled) {
                    setCatalog(data);
                    setCatalogError('');
                }
            } catch (err) {
                if (!cancelled) {
                    setCatalog(null);
                    setCatalogError(err.message || 'Could not load this activity.');
                }
            }
        };
        fetchCatalog();
        return () => { cancelled = true; };
    }, [form.activityId]);

    const primaryQuestion = catalog?.primary_question || null;
    const secondaryQuestion = catalog?.secondary_question || null;
    const combinations = catalog?.combinations || [];

    // Primary answers that lead to at least one supported combination.
    const primaryAnswers = useMemo(() => {
        if (!primaryQuestion) return [];
        const supported = new Set(combinations.map((c) => c.primary_answer_code));
        return primaryQuestion.answers.filter((a) => supported.has(a.answer_code));
    }, [primaryQuestion, combinations]);

    // Secondary answers restricted to combinations supported for the chosen primary answer.
    const secondaryAnswers = useMemo(() => {
        if (!secondaryQuestion) return [];
        const supported = new Set(
            combinations
                .filter((c) => (primaryQuestion ? c.primary_answer_code === form.primaryAnswerCode : true))
                .map((c) => c.secondary_answer_code)
        );
        return secondaryQuestion.answers.filter((a) => supported.has(a.answer_code));
    }, [secondaryQuestion, primaryQuestion, combinations, form.primaryAnswerCode]);

    // Re-validate the secondary answer whenever the primary answer changes.
    useEffect(() => {
        if (form.secondaryAnswerCode && !secondaryAnswers.some((a) => a.answer_code === form.secondaryAnswerCode)) {
            setForm((prev) => ({ ...prev, secondaryAnswerCode: null }));
        }
    }, [secondaryAnswers, form.secondaryAnswerCode]);

    const primaryDone = !primaryQuestion || Boolean(form.primaryAnswerCode);
    const secondaryDone = !secondaryQuestion || Boolean(form.secondaryAnswerCode);
    const questionsDone = Boolean(catalog) && primaryDone && secondaryDone;

    const durationSeconds = ((Number(form.hours) || 0) * 60 + (Number(form.minutes) || 0)) * 60;
    const durationDone = durationSeconds > 0;
    const effortDone = Number.isInteger(form.personalEffort) && form.personalEffort >= 1 && form.personalEffort <= 10;

    const canSubmit = Boolean(form.activityId) && questionsDone && durationDone && effortDone && !loading;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setLoading(true);
        setMessage('');
        try {
            const payload = {
                activity_id: form.activityId,
                primary_answer_code: primaryQuestion ? form.primaryAnswerCode : null,
                secondary_answer_code: secondaryQuestion ? form.secondaryAnswerCode : null,
                duration_seconds: durationSeconds,
                personal_effort: form.personalEffort,
                date: form.date,
            };

            const data = await logActivity(payload);
            setMessage('Activity logged successfully!');
            setForm({ ...INITIAL_FORM, date: getLocalDateString() });
            setCatalog(null);
            setSearchQuery('');
            if (onSuccess) onSuccess();

            const activityId = data.activity?.id ?? null;
            const earnedPoints = data.points ?? 0;
            const quote = SPUD_QUOTES[Math.floor(Math.random() * SPUD_QUOTES.length)];

            setPopup({ show: true, fadeOut: false, quote, points: earnedPoints, activityId });

            setTimeout(() => {
                setPopup((prev) => ({ ...prev, fadeOut: true }));
            }, 3200);

            setTimeout(() => {
                if (activityId) {
                    history.push(`/activity/${activityId}`);
                }
            }, 3500);
        } catch (err) {
            setMessage(err.message || 'Error logging activity');
        }
        setLoading(false);
    };

    const selectedActivity = catalog?.activity || null;

    return (
    <>
        <form onSubmit={handleSubmit} className="la-form">
            {/* 1. Activity */}
            <div className="la-field">
                <label className="la-label">🏃‍♂️ Activity</label>

                {form.activityId ? (
                    <div className="la-selected-activity">
                        <span className="la-selected-name">{displayName(form.activityId)}</span>
                        <button
                            type="button"
                            className="la-clear-activity"
                            onClick={handleClearActivity}
                            aria-label="Clear activity"
                        >
                            ×
                        </button>
                    </div>
                ) : (
                    <div className="la-search-wrapper" ref={searchWrapperRef}>
                        <input
                            type="text"
                            className="la-input la-search-input"
                            placeholder="Search activities…"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setSearchOpen(true);
                            }}
                            onFocus={() => setSearchOpen(true)}
                            autoComplete="off"
                        />
                        {searchOpen && (
                            <ul className="la-search-dropdown">
                                {searchResults.length === 0 ? (
                                    <li className="la-search-empty">No activities found</li>
                                ) : (() => {
                                    const hasQuery = searchQuery.trim().length > 0;
                                    const freqSet = new Set(frequentActivities);
                                    const freqResults = hasQuery ? [] : searchResults.filter((r) => freqSet.has(r.activity.activity_id));
                                    const otherResults = hasQuery ? searchResults : searchResults.filter((r) => !freqSet.has(r.activity.activity_id));

                                    const renderItem = ({ activity, matchedVia }) => (
                                        <li
                                            key={activity.activity_id}
                                            className="la-search-item"
                                            onMouseDown={() => handleSelectActivity(activity.activity_id)}
                                        >
                                            <span className="la-search-name">{activity.activity_name}</span>
                                            {matchedVia && (
                                                <span className="la-search-via">via {matchedVia}</span>
                                            )}
                                        </li>
                                    );

                                    return (
                                        <>
                                            {freqResults.length > 0 && (
                                                <>
                                                    <li className="la-search-group-label">Your Frequent Activities</li>
                                                    {freqResults.map(renderItem)}
                                                    {otherResults.length > 0 && <li className="la-search-divider" />}
                                                </>
                                            )}
                                            {otherResults.length > 0 && (
                                                <>
                                                    {!hasQuery && freqResults.length > 0 && (
                                                        <li className="la-search-group-label">All Activities</li>
                                                    )}
                                                    {otherResults.map(renderItem)}
                                                </>
                                            )}
                                        </>
                                    );
                                })()}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {form.activityId && catalogError && (
                <div className="sc-alert sc-alert--error">{catalogError}</div>
            )}

            {selectedActivity && (
                <div className="la-activity-card">
                    {selectedActivity.activity_description && (
                        <p className="la-activity-description">{selectedActivity.activity_description}</p>
                    )}
                    {selectedActivity.duration_basis && (
                        <div className="la-duration-basis">
                            <span className="la-duration-basis-pill">
                                ⏱️ {DURATION_BASIS_LABELS[selectedActivity.duration_basis] || selectedActivity.duration_basis}
                            </span>
                            {selectedActivity.duration_instructions && (
                                <span className="la-duration-instructions">{selectedActivity.duration_instructions}</span>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* 2. Primary question */}
            {catalog && primaryQuestion && (
                <AnswerChoiceGroup
                    name="primary_answer_code"
                    questionText={primaryQuestion.question_text}
                    answers={primaryAnswers}
                    value={form.primaryAnswerCode}
                    onChange={(code) => setForm((prev) => ({ ...prev, primaryAnswerCode: code }))}
                />
            )}

            {/* 3. Secondary question — only once the primary answer (if any) is chosen */}
            {catalog && secondaryQuestion && primaryDone && (
                <AnswerChoiceGroup
                    name="secondary_answer_code"
                    questionText={secondaryQuestion.question_text}
                    answers={secondaryAnswers}
                    value={form.secondaryAnswerCode}
                    onChange={(code) => setForm((prev) => ({ ...prev, secondaryAnswerCode: code }))}
                />
            )}

            {/* 4. Duration */}
            {questionsDone && (
                <div className="la-field">
                    <label className="la-label">⏱️ Duration</label>
                    <TimeScrollPicker
                        hours={form.hours}
                        minutes={form.minutes}
                        onHoursChange={(v) => setForm((prev) => ({ ...prev, hours: v }))}
                        onMinutesChange={(v) => setForm((prev) => ({ ...prev, minutes: v }))}
                    />
                    {!durationDone && (
                        <p className="la-help-text">Set a duration greater than zero.</p>
                    )}
                </div>
            )}

            {/* 5. Personal Effort */}
            {questionsDone && durationDone && (
                <EffortScale
                    value={form.personalEffort}
                    onChange={(v) => setForm((prev) => ({ ...prev, personalEffort: v }))}
                />
            )}

            {/* Optional, non-scoring details */}
            {questionsDone && (
                <details className="la-optional-details-section">
                    <summary className="la-optional-details-toggle">
                        <span className="la-optional-details-toggle-title">⚙️ Additional Details</span>
                        <span className="la-optional-hint">Optional</span>
                    </summary>
                    <div className="la-optional-details-content">
                        <div className="la-field">
                            <label className="la-label">📅 Date</label>
                            <input
                                name="date"
                                type="date"
                                value={form.date}
                                onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                                className="la-input"
                            />
                        </div>
                    </div>
                </details>
            )}

            <button
                type="submit"
                disabled={!canSubmit}
                className={`la-submit${loading ? ' loading' : ''}`}
            >
                {loading ? '⏳ Logging...' : '🚀 Log Activity'}
            </button>

            {message && (
                <div className={`la-message ${message.includes('Error') || !message.includes('success') ? 'error' : 'success'}`}>
                    {message}
                </div>
            )}
        </form>
        {popup.show && (
            <div className={`la-spud-overlay${popup.fadeOut ? ' la-spud-overlay--out' : ''}`}>
                <div className="la-spud-card">
                    <div className="la-spud-emoji">🥔</div>
                    <div className="la-spud-quote">{popup.quote}</div>
                    <div className="la-spud-points">+{popup.points} SP earned!</div>
                </div>
            </div>
        )}
    </>
    );
};

export default LogActivityForm;
